/**
 * StepPaletteModal.tsx - Full-screen modal for step type selection
 *
 * Displays the complete step palette with category filtering in a modal overlay.
 * Used when clicking the + button on chain terminus points to add new steps.
 *
 * Related:
 *   - components/ChainTerminusButton.tsx - Triggers this modal
 *   - components/paletteStepTypes.ts - Shared step type definitions
 *   - components/NodePalette.tsx - Sidebar version of the palette
 */

import { css } from 'aphrodite';
import { useCallback, useEffect, useState } from 'react';
import { Modal } from '../../../components/Modal';
import {
	type PaletteCategory,
	type PaletteNode,
	addableStepTypes,
	categoryColors,
	categoryFilters,
} from './paletteStepTypes';
import { StepIcon } from './StepIcon';
import { styleSheet } from './styles/StepPaletteModalStyles';

interface StepPaletteModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSelectStep: (stepType: string) => void;
	excludedStepTypes?: string[];
}

export function StepPaletteModal({ isOpen, onClose, onSelectStep, excludedStepTypes }: StepPaletteModalProps) {
	const [activeCategory, setActiveCategory] = useState<PaletteCategory>('all');

	// Reset to 'all' when modal opens
	useEffect(() => {
		if (isOpen) {
			setActiveCategory('all');
		}
	}, [isOpen]);

	// Handle ESC key
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, onClose]);

	const handleSelectStep = useCallback(
		(stepType: string) => {
			onSelectStep(stepType);
		},
		[onSelectStep]
	);

	const availableSteps = excludedStepTypes?.length
		? addableStepTypes.filter(node => !excludedStepTypes.includes(node.type))
		: addableStepTypes;

	const filteredNodes =
		activeCategory === 'all' ? availableSteps : availableSteps.filter(node => node.paletteCategory === activeCategory);

	return (
		<Modal isOpen={isOpen} onRequestClose={onClose}>
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.header)}>
					<h2 className={css(styleSheet.title)}>Add Step</h2>
					<p className={css(styleSheet.description)}>Select a step type to add to your automation</p>
				</div>

				{/* Category filters */}
				<div className={css(styleSheet.filterContainer)}>
					{categoryFilters.map(filter => (
						<button
							key={filter.id}
							type='button'
							className={css(styleSheet.filterButton, activeCategory === filter.id && styleSheet.filterButtonActive)}
							onClick={() => setActiveCategory(filter.id)}
						>
							{filter.label}
						</button>
					))}
				</div>

				{/* Step type grid */}
				<div className={css(styleSheet.grid)}>
					{filteredNodes.map(node => (
						<StepCard key={node.type} node={node} onSelect={handleSelectStep} />
					))}
				</div>

				{/* Cancel button */}
				<div className={css(styleSheet.actions)}>
					<button type='button' className={css(styleSheet.cancelButton)} onClick={onClose}>
						Cancel
					</button>
				</div>
			</div>
		</Modal>
	);
}

interface StepCardProps {
	node: PaletteNode;
	onSelect: (stepType: string) => void;
}

function StepCard({ node, onSelect }: StepCardProps) {
	const colors = categoryColors[node.visualCategory];

	return (
		<button
			type='button'
			className={css(styleSheet.stepCard)}
			style={{
				backgroundColor: colors.backgroundColor,
				borderColor: colors.borderColor,
				color: colors.color,
			}}
			onClick={() => onSelect(node.type)}
			aria-label={`Add ${node.label} step`}
		>
			<div className={css(styleSheet.stepHeader)}>
				<StepIcon category={node.visualCategory} />
				<span className={css(styleSheet.stepLabel)}>{node.label}</span>
			</div>
			<div className={css(styleSheet.stepDescription)}>{node.description}</div>
		</button>
	);
}
